/**
 * Game-register ability descriptions (playtest round 2, 2026-08-02).
 *
 * @pkmn's shortDesc strings are written for competitive Showdown play —
 * Rattata's Run Away shipped as "No competitive use." The audit found
 * ALL 11 in-scope abilities carry that register. These overrides speak
 * this game's language: 1-2 short sentences, payload front-loaded
 * (dossierIdentityHeader ellipsis-truncates), honest about what the
 * ability does IN THIS GAME — never a mechanical claim the game doesn't
 * implement (fleeing is unconditional for everyone today, so Run Away
 * gets flavor, not a fake guarantee).
 *
 * abilityDesc is denormalized into persisted records (pokedex snapshots,
 * career entries on poke:career which outlive Reset Run). The stored
 * ability ID makes read-time remap possible with zero schema bump —
 * gameAbilityDesc wires into BOTH write sites (draftRoll, offer
 * builders) AND both read-time projections (snapshotToOffer,
 * careerEntryToOffer), so stale persisted text dies at display time.
 *
 * ability-desc-overrides.test.ts walks every reachable species' ability
 * slots and asserts coverage — a new zone or a @pkmn bump (TODOs #13)
 * that surfaces an uncovered ability fails CI instead of leaking
 * Showdown register into the dossier.
 */
export const ABILITY_DESC_OVERRIDES: Record<string, string> = {
  runaway: 'A born escape artist — nothing holds it when it decides to bolt.',
  sturdy:
    'A body too solid to fell in one blow. One-hit knockout techniques never work on it.',
  keeneye:
    "Sharp eyes that can't be fooled. Moves like Sand Attack can't lower its accuracy.",
  guts: "Status conditions fire it up. Its Attack climbs by half, and a burn won't weaken its physical moves.",
  shielddust:
    'Powdery scales ward off the added effects of enemy attacks — no bonus poison, paralysis, or flinching.',
  rockhead:
    'A skull as hard as a boulder. It takes no recoil damage from its own reckless attacks.',
  shedskin:
    'Regularly sheds its skin — each turn it has a 1-in-3 chance of shaking off a status condition.',
  static:
    'Charged with static electricity. Attackers that make contact have a 1-in-3 chance of being paralyzed.',
  overgrow:
    'Powers up Grass-type moves in a pinch. Below a third of its HP, they hit 1.5x harder.',
  blaze:
    'Powers up Fire-type moves in a pinch. Below a third of its HP, they hit 1.5x harder.',
  torrent:
    'Powers up Water-type moves in a pinch. Below a third of its HP, they hit 1.5x harder.',
};

/** Resolve the description this game shows for an ability. Falls back
 *  to the dex shortDesc for anything the table doesn't cover (the
 *  coverage test keeps that set empty for reachable species). */
export function gameAbilityDesc(
  abilityId: string,
  dexShortDesc: string,
): string {
  return ABILITY_DESC_OVERRIDES[abilityId] ?? dexShortDesc;
}
